import { useState, useEffect } from 'react'
import { useAuth, getAuthToken } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { User, Building2, Loader2, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { API_BASE } from '@/services/api'

interface TenantConfig {
  company_name?: string
  domain?: string
  default_language?: string
}

// Module-level cache
let _cachedTenantConfig: TenantConfig | null = null

export default function SettingsPage() {
  const { user } = useAuth()
  const { tenantId } = useTenant()
  const [activeTab, setActiveTab] = useState('profile')

  // Profile form
  const [profileName, setProfileName] = useState(user?.name || '')
  const [savingProfile, setSavingProfile] = useState(false)

  // Company form
  const [companyName, setCompanyName] = useState(_cachedTenantConfig?.company_name || '')
  const [domain, setDomain] = useState(_cachedTenantConfig?.domain || '')
  const [defaultLanguage, setDefaultLanguage] = useState(_cachedTenantConfig?.default_language || 'EN')
  const [savingCompany, setSavingCompany] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(!_cachedTenantConfig)

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'company', label: 'Company', icon: Building2 },
  ]

  const getHeaders = async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token && token !== 'demo-token') {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  // Load tenant config on mount
  useEffect(() => {
    if (tenantId && !_cachedTenantConfig) {
      loadTenantConfig()
    }
  }, [tenantId])

  // Sync profile name from user
  useEffect(() => {
    if (user?.name) setProfileName(user.name)
  }, [user?.name])

  const loadTenantConfig = async () => {
    try {
      setLoadingConfig(true)
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/tenant/config`, { headers })
      if (response.ok) {
        const data = await response.json()
        const config: TenantConfig = {
          company_name: data.company_name || data.name || '',
          domain: data.domain || '',
          default_language: data.default_language || 'EN',
        }
        setCompanyName(config.company_name || '')
        setDomain(config.domain || '')
        setDefaultLanguage(config.default_language || 'EN')
        _cachedTenantConfig = config
      }
    } catch (err) {
      console.error('Failed to load tenant config:', err)
    } finally {
      setLoadingConfig(false)
    }
  }

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tenantId) return

    setSavingProfile(true)
    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/tenants/${tenantId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ contact_name: profileName }),
      })
      if (response.ok) {
        toast.success('Profile updated')
      } else {
        toast.error('Failed to save profile')
      }
    } catch {
      toast.error('Failed to save profile')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tenantId) return

    setSavingCompany(true)
    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/tenants/${tenantId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_name: companyName,
          domain,
          default_language: defaultLanguage,
        }),
      })
      if (response.ok) {
        _cachedTenantConfig = { company_name: companyName, domain, default_language: defaultLanguage }
        toast.success('Company settings updated')
      } else {
        toast.error('Failed to save settings')
      }
    } catch {
      toast.error('Failed to save settings')
    } finally {
      setSavingCompany(false)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account and company preferences</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-56 flex-shrink-0">
          <nav className="bg-white rounded-xl shadow-sm border border-gray-100 p-1.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-3.5 py-2.5 rounded-lg transition-colors text-sm ${
                  activeTab === tab.id
                    ? 'bg-amber-50 text-amber-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50 font-medium'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-6">Profile Information</h2>

              <div className="flex items-center space-x-5 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/10">
                  <span className="text-2xl font-bold text-gray-900">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{user?.email || 'No email'}</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleSaveProfile}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      defaultValue={user?.email}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-500"
                      disabled
                    />
                  </div>
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="flex items-center px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                  >
                    {savingProfile ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Check className="w-3.5 h-3.5 mr-2" /> Save Changes</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'company' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-6">Company Settings</h2>
              {loadingConfig ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSaveCompany}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Your company name"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Domain</label>
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="yourstore.com"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Language</label>
                    <select
                      value={defaultLanguage}
                      onChange={(e) => setDefaultLanguage(e.target.value)}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                    >
                      <option value="EN">English</option>
                      <option value="NO">Norwegian (Bokmal)</option>
                      <option value="DA">Danish</option>
                      <option value="SV">Swedish</option>
                    </select>
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={savingCompany}
                      className="flex items-center px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                    >
                      {savingCompany ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Check className="w-3.5 h-3.5 mr-2" /> Save Changes</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
